import { Component, OnInit, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormGroup, FormBuilder, Validators, NG_VALUE_ACCESSOR, FormArray, AbstractControl } from '@angular/forms';
import { MedicalFeeAmount } from '../../../../model/medical-fee/medical-fee-amount';
import { MedicalFeePeriodFilter } from '../../../../model/medical-fee/medical-fee-period-filter';
import { MedicalFeeService } from '../../../../services/medical-fee/medical-fee.service';
import { FilterCompleteService } from 'src/app/filter-complete/filter-complete';
import { PhysicianFilter } from 'src/app/model/physician-Filter';
import { FilterService } from '../../../../services/filter.service';
import { TaxpayerType } from '../../../../model/taxpayer-type';
import { MatTableDataSource, MatDialog } from '@angular/material';
import { MedicalFeeWithholdTaxAmount } from '../../../../model/medical-fee/medical-fee-withhold-tax-amount';
import { BehaviorSubject } from 'rxjs';
import { MedicalFeeWithholdTaxDialogComponent, MedicalFeeWithholdTaxDialogResponse } from '../../../dialogs/medical-fee-withhold-tax-dialog/medical-fee-withhold-tax-dialog.component';
import { MedicalFeePaymentService } from '../../../../services/medical-fee/medical-fee-payment.service';
import { MedicalFeePracticeTaxAmount } from '../../../../model/medical-fee/medical-fee-practice-tax-amount';
import { MedicalFeeTaxAmount } from '../../../../model/medical-fee/medical-fee-tax-amount';
import { MedicalFeeTaxAmountDialogComponent } from '../../../dialogs/medical-fee-tax-amount-dialog/medical-fee-tax-amount-dialog.component';
import { ValidateAutoCompleteSelect } from '../../../../validators/autocomplete-select.validator';
import { MedicalFeeAmountService } from '../../../../services/medical-fee/medical-fee-amount.service';

export const MEDICAL_FEE_AMOUNT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => MedicalFeeAmountComponent),
  multi: true,
};

@Component({
  selector: 'app-medical-fee-amount',
  templateUrl: './medical-fee-amount.component.html',
  styleUrls: ['./medical-fee-amount.component.css'],
  providers: [ MEDICAL_FEE_AMOUNT_VALUE_ACCESSOR ]
})
export class MedicalFeeAmountComponent implements ControlValueAccessor {

  onChange;
  form: FormGroup;
  medicalFeePeriodFilters: MedicalFeePeriodFilter[];
  physicians: PhysicianFilter[];
  taxpayerTypes: TaxpayerType[];
  medicalFeeYearPeriods: number[] = [];
  filterCompletePhysician: FilterCompleteService<PhysicianFilter>;
  medicalFeePracticeTaxAmounts: MedicalFeePracticeTaxAmount[] = [];

  totalPracticeTaxAmountBase: number;
  totalPracticeTaxAmount: number;
  medicalFeeTaxAmount: number;
  medicalFeeTaxAmountBase: number;
  medicalFeeWithholdTaxAmount: number;
  medicalFeeTotal: number;


  medicalFeeWithholdTaxAmountDataSource = new BehaviorSubject<AbstractControl[]>([]);
  medicalFeeWithholdTaxAmountDisplayedColumns: string[] = [
    'TaxName', 'Aliquot', 'Amount', 'Delete'
  ];

  medicalFeeTaxAmountDataSource = new BehaviorSubject<AbstractControl[]>([]);
  medicalFeeTaxAmountDisplayedColumns: string[] = [
    'TaxName', 'Aliquot', 'Amount', 'AmountBase', 'Description', 'Delete'
  ];

  medicalFeePracticeTaxAmountDataSource = new MatTableDataSource([]);
  medicalFeePracticeTaxAmountDisplayedColumns: string[] = [
    'TaxName', 'Aliquot', 'Amount', 'AmountBase'
  ];

  constructor( private formBuilder: FormBuilder,
               private filterService: FilterService,
               private dialog: MatDialog,
               private mfaService: MedicalFeeAmountService,
               private mfpService: MedicalFeePaymentService,
               private medicalFeeService: MedicalFeeService ) {

    this.form = this.formBuilder.group({

      Number :  [ { value: '', disabled: true }, Validators.required ],
      Physician : [ { value: '' }, [ Validators.required, ValidateAutoCompleteSelect ] ],
      MedicalFeePeriod : [ '' ],
      MedicalFeeYearPeriod : [ '' ],
      TotalPracticeTaxAmountBase: [ '' ],
      TotalPracticeTaxAmount: [ '' ],
      MedicalFeeTaxAmount: [ '' ],
      MedicalFeeTaxAmountBase: [ '' ],
      MedicalFeeWithholdTaxAmount: [ '' ],
      MedicalFeeTotal: [ '' ],
      PayDate : [ '', Validators.required ],
      TaxpayerType : [ { value: '', disabled: true }, Validators.required ],
      MedicalFeeWithholdTaxAmounts : this.formBuilder.array([]),
      MedicalFeeTaxAmounts : this.formBuilder.array([])

    });

    this.filterCompletePhysician =
      new FilterCompleteService<PhysicianFilter>([], this.form.controls.Phisician);

    this.form.valueChanges.subscribe(() => this.emitChange() );
    this.fMedicalFeeWithholdTaxAmounts.valueChanges.subscribe(() => this.emitChange() );
    this.fMedicalFeeTaxAmounts.valueChanges.subscribe(() => this.emitChange() );

    this.form.controls.Physician.valueChanges.subscribe( (value) => {
      this.loadTaxpayerTypes( value &&  typeof value !== 'string' ? value.TaxpayerTypeId : null );
    });
  }

  get fMedicalFeeTaxAmounts() {
    return this.form.get('MedicalFeeTaxAmounts') as FormArray;
  }

  get fMedicalFeeWithholdTaxAmounts() {
    return this.form.get('MedicalFeeWithholdTaxAmounts') as FormArray;
  }

  get medicalFeeTotalText() { return `Total final a liquidar: ${ this.medicalFeeTotal }`; }

  get f() { return this.form.controls; }

  get filteredPhysicianOptions() { return this.filterCompletePhysician ?
    this.filterCompletePhysician.filteredOptions : []; }

  async writeValue(obj: MedicalFeeAmount) {

    await this.loadInfo();

    if ( obj ) {

      const period = obj.MedicalFeePeriod ? this
        .medicalFeePeriodFilters.find( p => p.Id === obj.MedicalFeePeriod.Id ) : null;

      const year = obj.MedicalFeeYearPeriod ? this
        .medicalFeeYearPeriods.find( p => p === obj.MedicalFeeYearPeriod ) : null;
    
      if (!year) {
        this.medicalFeeYearPeriods.push( obj.MedicalFeeYearPeriod )
      }

      this.form.patchValue({ 
        Number : obj.Number,
        Physician : obj.Physician,
        MedicalFeePeriod : period,
        MedicalFeeYearPeriod: obj.MedicalFeeYearPeriod  == null ? new Date().getFullYear() : obj.MedicalFeeYearPeriod ,
        PayDate : obj.PayDate
      });
 
      this.loadAmounts( obj );

    }
  }

  loadAmounts( medicalFeeAmount: MedicalFeeAmount ) {

    this.addMedicalFeeWithholdTaxs( medicalFeeAmount.MedicalFeeWithholdTaxAmounts );
    this.addMedicalFeeTaxs( medicalFeeAmount.MedicalFeeTaxAmounts );
    this.medicalFeePracticeTaxAmountDataSource =
      new MatTableDataSource( medicalFeeAmount.MedicalFeePracticeTaxAmounts );
    this.medicalFeePracticeTaxAmounts = medicalFeeAmount.MedicalFeePracticeTaxAmounts;

    if ( this.medicalFeePracticeTaxAmounts.length > 0 ) {
      this.form.controls['Physician'].disable();
    }

    this.form.patchValue({
      TotalPracticeTaxAmountBase: medicalFeeAmount.TotalPracticeTaxAmountBase,
      TotalPracticeTaxAmount: medicalFeeAmount.TotalPracticeTaxAmount,
      MedicalFeeTaxAmount: medicalFeeAmount.MedicalFeeTaxAmount,
      MedicalFeeTaxAmountBase: medicalFeeAmount.MedicalFeeTaxAmountBase,
      MedicalFeeWithholdTaxAmount: medicalFeeAmount.MedicalFeeWithholdTaxAmount,
      MedicalFeeTotal: medicalFeeAmount.MedicalFeeTotal
    });

    this.totalPracticeTaxAmountBase = medicalFeeAmount.TotalPracticeTaxAmountBase;
    this.totalPracticeTaxAmount = medicalFeeAmount.TotalPracticeTaxAmount;
    this.medicalFeeTaxAmount = medicalFeeAmount.MedicalFeeTaxAmount;
    this.medicalFeeTaxAmountBase = medicalFeeAmount.MedicalFeeTaxAmountBase;
    this.medicalFeeWithholdTaxAmount = medicalFeeAmount.MedicalFeeWithholdTaxAmount;
    this.medicalFeeTotal = medicalFeeAmount.MedicalFeeTotal;

  }

  addMedicalFeeWithholdTaxs( medicalFeeWithholdTaxAmounts: MedicalFeeWithholdTaxAmount[] ) {

    this.form.controls.MedicalFeeWithholdTaxAmounts = this.formBuilder.array([]);

    medicalFeeWithholdTaxAmounts.forEach( (medicalFeeWithholdTaxAmount) => {

      this.fMedicalFeeWithholdTaxAmounts.push( this.formBuilder.group({
        Id: medicalFeeWithholdTaxAmount.Id,
        MedicalFeeWithholdTaxName: medicalFeeWithholdTaxAmount.MedicalFeeWithholdTaxName,
        Aliquot: medicalFeeWithholdTaxAmount.Aliquot,
        Amount: medicalFeeWithholdTaxAmount.Amount,
        WithholdTaxId: medicalFeeWithholdTaxAmount.WithholdTaxId
      }));

      this.medicalFeeWithholdTaxAmountDataSource.next(
        this.fMedicalFeeWithholdTaxAmounts.controls);
    });
  }

  addMedicalFeeTaxs( medicalFeeTaxAmounts: MedicalFeeTaxAmount[] ) {

    this.form.controls.MedicalFeeTaxAmounts = this.formBuilder.array([]);

    medicalFeeTaxAmounts.forEach( (medicalFeeTaxAmount) => {

      this.fMedicalFeeTaxAmounts.push( this.formBuilder.group({
        AliquotTax: medicalFeeTaxAmount.AliquotTax,
        Amount: medicalFeeTaxAmount.Amount,
        AmountBase: medicalFeeTaxAmount.AmountBase,
        TaxType: medicalFeeTaxAmount.TaxType,
        Description: medicalFeeTaxAmount.Description
      }));

      this.medicalFeeTaxAmountDataSource.next(
        this.fMedicalFeeTaxAmounts.controls);
    });
  }

  async loadInfo() {

    if ( !this.medicalFeePeriodFilters ) {
      await this.loadMedicalFeePeriods();
    }

    if (this.medicalFeeYearPeriods.length === 0) {
       let year = new Date().getFullYear() - 10; 
       let to = new Date().getFullYear()  + 2; 
       while(year <= to) {
        this.medicalFeeYearPeriods.push(year);
        year++;
       }
       
       
    }

    if ( !this.physicians ) {
      await this.loadPhysicians();
    }
  }

  private loadTaxpayerTypes( taxpayerTypeId?: number ) {

    this.form.patchValue({ TaxpayerType: null });

    const prom = this.taxpayerTypes ?
      new Promise<TaxpayerType[]>((resolve) => resolve(this.taxpayerTypes)) :
        this.filterService.getTaxpayerType();

    prom.then( ( items ) => {
        this.taxpayerTypes = items;
        if ( taxpayerTypeId ) {
          const seleted = this.taxpayerTypes.find(m => m.Id === taxpayerTypeId);
          this.form.patchValue({ TaxpayerType: seleted });
        }
    });
  }

  private async loadPhysicians() {
    this.physicians = await this.filterService.getPhysiciansFilter();
    this.filterCompletePhysician = new FilterCompleteService<PhysicianFilter>(
      this.physicians, this.form.controls.Physician);
  }

  private async loadMedicalFeePeriods() {
    this.medicalFeePeriodFilters =
      await this.medicalFeeService.getMedicalFeePeriodFilters();
  }

  private getMedicalFeeAmount() {

    const medicalFeeAmount = <MedicalFeeAmount>this.form.getRawValue();
    medicalFeeAmount.MedicalFeePracticeTaxAmounts = this.medicalFeePracticeTaxAmounts;
    return medicalFeeAmount;
  }

  async deleteFeeWithholdTax( index: number ) {

    this.fMedicalFeeWithholdTaxAmounts.removeAt( index );
    this.medicalFeeWithholdTaxAmountDataSource.next(
      this.fMedicalFeeWithholdTaxAmounts.controls);
    await this.recalculateAmounts(this.getMedicalFeeAmount());
  }

  async deleteMedicalFeeTax( index: number ) {

    this.fMedicalFeeTaxAmounts.removeAt( index );
    this.medicalFeeTaxAmountDataSource.next(
      this.fMedicalFeeTaxAmounts.controls);
    await this.recalculateAmounts(this.getMedicalFeeAmount());
  }

  showMedicalFeeTaxDialog() {

    const dialogRef = this.dialog.open(MedicalFeeTaxAmountDialogComponent, {
      width: '50%',
      data: {}
    });

    dialogRef.afterClosed().subscribe( async ( info: MedicalFeeTaxAmount ) => {

         if ( info ) {
          const medicalFeeAmount = this.getMedicalFeeAmount();
          medicalFeeAmount.MedicalFeeTaxAmounts.push(info);
          await this.recalculateAmounts(medicalFeeAmount);
         }
    });
  }

  showMedicalFeeWithholdTaxDialog() {

    const amounts = this.getMedicalFeeAmount();
    const ids = amounts.MedicalFeeWithholdTaxAmounts.map(m => m.WithholdTaxId);

    const dialogRef = this.dialog.open(MedicalFeeWithholdTaxDialogComponent, {
      width: '50%',
      data: ids
    });

    dialogRef.afterClosed().subscribe( async ( info: MedicalFeeWithholdTaxDialogResponse ) => {

        if ( info ) {
          const medicalFeeAmount = this.getMedicalFeeAmount();
          const tax = new MedicalFeeWithholdTaxAmount();
          tax.Aliquot = info.Aliquot;
          tax.MedicalFeeWithholdTaxName = info.MedicalFeeWithholdTax.Name;
          tax.WithholdTaxId = info.MedicalFeeWithholdTax.Id;
          medicalFeeAmount.MedicalFeeWithholdTaxAmounts.push(tax);
          await this.recalculateAmounts(medicalFeeAmount);
        }
    });
  }

  private async recalculateAmounts(medicalFeeAmount: MedicalFeeAmount) {
    medicalFeeAmount = await this.mfaService
      .recalculateMedicaFeeAmounts(medicalFeeAmount);
    this.loadAmounts(medicalFeeAmount);
    return medicalFeeAmount;
  }

  emitChange() {

    if ( this.onChange ) {
      this.onChange(this.getMedicalFeeAmount());
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {

  }

  setDisabledState?(isDisabled: boolean): void {

  }

  isValid() {

    Object.values( this.form.controls ).forEach( control => {
      control.markAsTouched();
    });

    return this.form.valid;
  }
}
